import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/apiService';
import {
    Alert,
    Avatar,
    Badge,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    FormField,
    FormGrid,
    Input,
    PageHeader,
} from '../components/ui';

const Profile = () => {
    const { user, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    const [details, setDetails] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        phone: user?.phone || '',
        department: user?.department || '',
    });

    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const handleDetailChange = (e) => {
        setDetails({ ...details, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleUpdateDetails = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await authAPI.updateDetails(details);
            updateUser(response.data.data);
            toast.success('Profile updated');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Update failed');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');

        if (passwords.newPassword.length < 6) {
            setPasswordError('Use at least 6 characters.');
            return;
        }
        if (passwords.newPassword !== passwords.confirmPassword) {
            setPasswordError('The two new passwords do not match.');
            return;
        }

        setPasswordLoading(true);
        try {
            await authAPI.updatePassword({
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword,
            });
            toast.success('Password updated');
            setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Password update failed');
        } finally {
            setPasswordLoading(false);
        }
    };

    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

    return (
        <div className="max-w-5xl">
            <PageHeader
                title="Account settings"
                description="Your personal details and sign-in security."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Profile' }]}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                {/* Identity */}
                <Card className="lg:sticky lg:top-[calc(var(--topbar-h)+16px)]">
                    <CardBody className="text-center">
                        <Avatar name={fullName} size="xl" className="mx-auto" />
                        <h2 className="text-lg font-semibold text-ink-900 mt-3">{fullName}</h2>
                        <p className="text-base text-ink-500 truncate">{user?.email}</p>

                        <div className="flex items-center justify-center gap-2 mt-3">
                            <Badge variant="primary">{user?.role}</Badge>
                            {user?.department && <Badge variant="neutral">{user.department}</Badge>}
                        </div>
                    </CardBody>

                    {user?.created_at && (
                        <CardFooter>
                            <p className="text-label text-ink-500 text-center">
                                Member since{' '}
                                {new Date(user.created_at).toLocaleDateString(undefined, {
                                    month: 'long',
                                    year: 'numeric',
                                })}
                            </p>
                        </CardFooter>
                    )}
                </Card>

                <div className="lg:col-span-2 space-y-4">
                    {/* Details */}
                    <Card>
                        <CardHeader
                            title="Personal details"
                            subtitle="Shown to colleagues across projects and tasks."
                        />
                        <form onSubmit={handleUpdateDetails}>
                            <CardBody className="space-y-4">
                                <FormGrid>
                                    <FormField label="First name" required htmlFor="profile-first">
                                        <Input
                                            id="profile-first"
                                            name="first_name"
                                            value={details.first_name}
                                            onChange={handleDetailChange}
                                            required
                                        />
                                    </FormField>

                                    <FormField label="Last name" required htmlFor="profile-last">
                                        <Input
                                            id="profile-last"
                                            name="last_name"
                                            value={details.last_name}
                                            onChange={handleDetailChange}
                                            required
                                        />
                                    </FormField>
                                </FormGrid>

                                <FormGrid>
                                    <FormField
                                        label="Email address"
                                        htmlFor="profile-email"
                                        hint="Contact an administrator to change your email."
                                    >
                                        <Input id="profile-email" type="email" value={user?.email || ''} disabled />
                                    </FormField>

                                    <FormField label="Phone number" htmlFor="profile-phone">
                                        <Input
                                            id="profile-phone"
                                            type="tel"
                                            name="phone"
                                            value={details.phone}
                                            onChange={handleDetailChange}
                                            placeholder="+252 …"
                                        />
                                    </FormField>
                                </FormGrid>
                            </CardBody>

                            <CardFooter className="flex justify-end">
                                <Button type="submit" variant="primary" loading={loading}>
                                    Save changes
                                </Button>
                            </CardFooter>
                        </form>
                    </Card>

                    {/* Password */}
                    <Card>
                        <CardHeader
                            title="Password"
                            subtitle="Use a unique password you don't reuse elsewhere."
                        />
                        <form onSubmit={handleUpdatePassword}>
                            <CardBody className="space-y-4">
                                {passwordError && <Alert variant="danger">{passwordError}</Alert>}

                                <FormField label="Current password" required htmlFor="profile-current">
                                    <Input
                                        id="profile-current"
                                        type="password"
                                        name="currentPassword"
                                        value={passwords.currentPassword}
                                        onChange={handlePasswordChange}
                                        autoComplete="current-password"
                                        required
                                    />
                                </FormField>

                                <FormGrid>
                                    <FormField
                                        label="New password"
                                        required
                                        htmlFor="profile-new"
                                        hint="At least 6 characters."
                                    >
                                        <Input
                                            id="profile-new"
                                            type="password"
                                            name="newPassword"
                                            value={passwords.newPassword}
                                            onChange={handlePasswordChange}
                                            autoComplete="new-password"
                                            required
                                        />
                                    </FormField>

                                    <FormField label="Confirm new password" required htmlFor="profile-confirm">
                                        <Input
                                            id="profile-confirm"
                                            type="password"
                                            name="confirmPassword"
                                            value={passwords.confirmPassword}
                                            onChange={handlePasswordChange}
                                            autoComplete="new-password"
                                            required
                                        />
                                    </FormField>
                                </FormGrid>
                            </CardBody>

                            <CardFooter className="flex justify-end">
                                <Button type="submit" variant="primary" loading={passwordLoading}>
                                    Update password
                                </Button>
                            </CardFooter>
                        </form>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Profile;
